import { AlertTriangle, RefreshCw, RotateCw } from "lucide-react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useRetriggerProcessing, useWalkthroughsByStatus } from "@/api/hooks";
import type { WalkthroughResponse } from "@/api/types";
import { formatBytes, formatTime, shortId } from "@/lib/format";

/** Failed-walkthrough triage view for staff.
 *
 *  Reads ``GET /walkthroughs?status=failed`` and surfaces every
 *  walkthrough whose AI pipeline blew up so support can re-run it
 *  (or contact the contractor) without grepping container logs.
 *
 *  "Re-run AI" hits ``POST /walkthroughs/{id}/process`` (alias for
 *  finalize) — counts as a fresh chargeable AI run, same as
 *  contractor-initiated retries. */
export function FailedWalkthroughsPage() {
  const failed = useWalkthroughsByStatus("failed", 200);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">Failed walkthroughs</h1>
        <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
          Every walkthrough whose AI pipeline crashed or returned an error. Newest first. Use
          "Re-run AI" to retry from here without poking around the contractor's app — counts as a
          fresh chargeable run, just like a contractor-initiated retry.
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => failed.refetch()}
          disabled={failed.isFetching}
          className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-40"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${failed.isFetching ? "animate-spin" : ""}`}
            aria-hidden
          />
          Refresh
        </button>
      </div>

      {failed.isLoading && (
        <p className="text-sm text-graphite-soft">Loading failed walkthroughs…</p>
      )}
      {failed.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {failed.error instanceof ApiError
            ? failed.error.detail
            : "Could not load failed walkthroughs."}
        </p>
      )}
      {failed.data && failed.data.length === 0 && (
        <p className="rounded-lg border border-rule bg-paper-warm p-5 text-sm text-graphite-soft">
          No failed walkthroughs. (Quiet is good — but if a contractor reported one, the cron may
          have already cleaned it up. Check Audit log filtered by walkthrough id.)
        </p>
      )}

      {failed.data && failed.data.length > 0 && (
        <ul className="space-y-2">
          {failed.data.map((w) => (
            <FailedRow key={w.id} walkthrough={w} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FailedRow({ walkthrough }: { walkthrough: WalkthroughResponse }) {
  const retrigger = useRetriggerProcessing(walkthrough.id);

  const totalBytes =
    walkthrough.clips?.reduce((sum, c) => sum + c.size_bytes, 0) ?? walkthrough.size_bytes;
  const clipCount = walkthrough.clip_count ?? walkthrough.clips?.length ?? 1;

  return (
    <li className="rounded-lg border border-red-pencil/30 bg-red-pencil/5 p-4">
      <div className="flex flex-wrap items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-red-pencil" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Link
              to={`/walkthroughs/${walkthrough.id}`}
              className="font-mono text-sm font-semibold underline-offset-2 hover:underline"
            >
              {clipCount > 1 ? `${clipCount}-clip walkthrough` : walkthrough.filename}
            </Link>
            <span className="font-mono text-xs text-graphite-soft tabular-nums">
              {shortId(walkthrough.id)}
            </span>
            <span className="font-mono text-xs text-graphite-soft tabular-nums">
              created {formatTime(walkthrough.created_at)}
            </span>
            <span className="font-mono text-xs text-graphite-soft tabular-nums">
              {formatBytes(totalBytes)}
            </span>
          </div>
          {walkthrough.error_message && (
            <p className="mt-2 rounded border border-red-pencil/40 bg-paper px-3 py-2 font-mono text-xs text-red-pencil">
              {walkthrough.error_message}
            </p>
          )}
          {!walkthrough.error_message && (
            <p className="mt-2 font-mono text-xs italic text-graphite-faint">
              No error message recorded.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => retrigger.mutate()}
          disabled={retrigger.isPending}
          className="inline-flex items-center gap-1.5 rounded border border-graphite bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-graphite hover:text-paper disabled:opacity-40"
        >
          <RotateCw className="h-3.5 w-3.5" aria-hidden />
          {retrigger.isPending ? "Re-running…" : "Re-run AI"}
        </button>
      </div>
      {retrigger.error && (
        <p className="mt-2 text-xs text-red-pencil">
          {retrigger.error instanceof ApiError ? retrigger.error.detail : "Re-run failed."}
        </p>
      )}
    </li>
  );
}
